import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { GoogleLogo, LinkSimple, LinkBreak, CheckCircle } from "@phosphor-icons/react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleLink() {
  const redirectUrl = window.location.origin + "/auth/callback?intent=link";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

/**
 * Card shown in Réglages to let the current user link/unlink their Google
 * account. Uses the same Emergent OAuth flow as /login but redirects back
 * with `?intent=link` so AuthCallback hits /api/auth/google/link instead of
 * /api/auth/google/session.
 */
export default function GoogleAccountCard() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const linked = !!user?.google_sub;

  const unlink = async () => {
    if (!window.confirm("Délier ton compte Google ? Tu pourras toujours te connecter par téléphone + mot de passe.")) return;
    setBusy(true);
    try {
      await api.post("/auth/google/unlink");
      toast.success("Compte Google délié");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 flex items-center gap-3" data-testid="google-account-card">
      <div className="w-10 h-10 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center">
        <GoogleLogo size={20} weight="bold" className="text-white/85" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium flex items-center gap-1.5">
          Compte Google
          {linked && <CheckCircle size={14} weight="fill" className="text-emerald-400" />}
        </div>
        <div className="text-[11px] text-white/50 truncate" data-testid="google-account-status">
          {linked
            ? `Lié à ${user.email || "ton compte Google"}`
            : "Se connecter d'un tap depuis /login"}
        </div>
      </div>
      {linked ? (
        <Button
          type="button"
          onClick={unlink}
          disabled={busy}
          variant="ghost"
          className="h-9 border border-white/10 text-white/70 hover:bg-red-500/10 hover:text-red-300 rounded-xl px-3 text-xs"
          data-testid="google-unlink-btn"
        >
          <LinkBreak size={14} /> Délier
        </Button>
      ) : (
        <Button
          type="button"
          onClick={startGoogleLink}
          disabled={busy}
          className="h-9 bg-white text-[#0A0A0A] hover:bg-white/90 rounded-xl px-3 text-xs font-semibold"
          data-testid="google-link-btn"
        >
          <LinkSimple size={14} weight="bold" /> Lier Google
        </Button>
      )}
    </div>
  );
}
